import React, { createContext, useContext, useState, useCallback } from "react";
import type { Event } from "@/types/events";

interface EventDialogContextType {
  isOpen: boolean;
  mode: "create" | "edit";
  editingEvent: Event | null;
  openCreateDialog: () => void;
  openEditDialog: (event: Event) => void;
  closeDialog: () => void;
}

const EventDialogContext = createContext<EventDialogContextType | undefined>(undefined);

export const EventDialogProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [mode, setMode] = useState<"create" | "edit">("create");
  const [editingEvent, setEditingEvent] = useState<Event | null>(null);

  const openCreateDialog = useCallback(() => {
    setMode("create");
    setEditingEvent(null);
    setIsOpen(true);
  }, []);

  const openEditDialog = useCallback((event: Event) => {
    setMode("edit");
    setEditingEvent(event);
    setIsOpen(true);
  }, []);

  const closeDialog = useCallback(() => {
    setIsOpen(false);
    setTimeout(() => setEditingEvent(null), 200);
  }, []);

  return (
    <EventDialogContext.Provider
      value={{
        isOpen,
        mode,
        editingEvent,
        openCreateDialog,
        openEditDialog,
        closeDialog,
      }}
    >
      {children}
    </EventDialogContext.Provider>
  );
};

export const useEventDialog = () => {
  const context = useContext(EventDialogContext);
  if (!context) {
    throw new Error("useEventDialog must be used within EventDialogProvider");
  }
  return context;
};
